import * as React from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import { Avatar, Box, Typography, Paper, Divider, Chip, Stack } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { viewPlayer, viewPlayerPerformance } from '../../../../Utils/services';
import { getInitialsForName, getRandomColorCode, isUndefined } from '../../../../Utils/utils';
import Loader from '../../../../Shared/Progress/loader';
import { performance_default_values } from '../CreatePlayers/data/createPlayerCreationData';

const ViewPlayers = () => {
    const navigate = useNavigate();
    let { path_player_id } = useParams();
    const [viewEmployeeInfo, setEmployeeInfo] = useState({});
    const [empPerfInfo, setPerfInfo] = useState({});
    const [loadSpinner, setLoadSpinner] = useState(false);
    const [uploadFile, setUpload] = useState('');

    useEffect(() => {
        getPlayerInfo();
    }, [])

    async function getPlayerInfo() {
        setLoadSpinner(true)
        let param = { player_id: path_player_id }
        await viewPlayer(param).then((res) => {
            let tempres = res.body;
            setEmployeeInfo(tempres.PlayerDetails || {});
            setUpload(tempres.PlayerDetails?.player_photo || '');
            setPerfInfo(tempres.PlayerPerformance?.[0] || {})
            setLoadSpinner(false)
        }).catch(() => setLoadSpinner(false))
    }

    function playerPage() {
        navigate('/Players');
    }

    function editPlayer() {
        navigate('/Players/player/' + path_player_id);
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            <Loader show={loadSpinner} />

            {/* Header with back button and title */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Button
                    variant="outlined"
                    onClick={() => playerPage()}
                    sx={{ textTransform: 'none' }}
                >
                    Back to Players
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    Player Profile
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => editPlayer()}
                    sx={{ textTransform: 'none' }}
                >
                    Edit Player
                </Button>
            </Box>

            {/* Main profile section */}

            <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Grid container spacing={4}>
                    {/* Player image/avatar */}
                    <Grid item xs={12} md={3}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            {viewEmployeeInfo.player_photo ? (
                                <Box
                                    component="img"
                                    src={viewEmployeeInfo.player_photo}
                                    alt={viewEmployeeInfo.player_name}
                                    sx={{
                                        width: '100%',
                                        maxWidth: 240,
                                        height: 'auto',
                                        borderRadius: '8px',
                                        boxShadow: 2
                                    }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <Avatar
                                    sx={{
                                        bgcolor: getRandomColorCode(),
                                        width: 120,
                                        height: 120,
                                        fontSize: 50,
                                        boxShadow: 2
                                    }}
                                >
                                    {getInitialsForName(viewEmployeeInfo.player_name)}
                                </Avatar>
                            )}

                            <Chip
                                label={viewEmployeeInfo.status}
                                color={viewEmployeeInfo.status === 'Active' ? 'success' : 'default'}
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>
                    </Grid>

                    {/* Player details */}
                    <Grid item xs={12} md={9}>
                        <Typography variant="h3" component="h2" sx={{ mb: 1, fontWeight: 700 }}>
                            {viewEmployeeInfo.player_name}
                        </Typography>

                        <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
                            {viewEmployeeInfo.position} • {viewEmployeeInfo.team_name}
                        </Typography>

                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="College"
                                    value={viewEmployeeInfo.college}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Class"
                                    value={viewEmployeeInfo.player_class}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="League"
                                    value={viewEmployeeInfo.sports_name}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Games Played"
                                    value={viewEmployeeInfo.number_of_games}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* Draft and stock information */}
                        {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Draft & Stock Information
                        </Typography> */}

                        <Grid container spacing={3}>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Round"
                                    value={viewEmployeeInfo.round}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Pick Number(Ovr)"
                                    value={viewEmployeeInfo.overall_pick}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Draft Price"
                                    value={`$${viewEmployeeInfo.draft_price}`}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Stock Price"
                                    value={`$${viewEmployeeInfo.new_stock_value}`}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Allocated Stocks"
                                    value={viewEmployeeInfo.allocated_stocks}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Stocks Sold"
                                    value={viewEmployeeInfo.stocks_sold}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Available Stocks"
                                    value={viewEmployeeInfo.available_stocks_in_stocklete_bucket}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DetailItem
                                    label="Grade"
                                    value={viewEmployeeInfo.grade}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {/* Performance section */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 600 }}>
                    Performance Metrics
                </Typography>

                <Grid container spacing={2}>
                    {performance_default_values().map((perf) => (
                        <Grid item xs={6} sm={4} md={2} key={perf.param}>
                            <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {perf.playerStatic}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {empPerfInfo[perf.param] || '-'}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        </Box>
    )
}

// Reusable detail item component
const DetailItem = ({ label, value }) => (
    <Box>
        <Typography variant="subtitle2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {value || '-'}
        </Typography>
    </Box>
);

export default ViewPlayers